import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Plus } from "lucide-react";

import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";

import { listVenues } from "../../../pages/SuperAdmin/Venues/venuesApi";
import {
  updateGlobalCommission,
  updateVenueCommission,
  getEffectiveCommission,
  getGlobalCommission,
  listVenueCommissionOverrides,
} from "../api";

const CommissionPage = () => {
  // --- Save-state styles (exactly like your previous snippet) ---
  const styles = `
    .ok { color: #28a745; font-weight: 600; margin-left: 8px; }
    .err { color: #d93025; font-weight: 600; margin-left: 8px; }
    .sa-actions { display:flex; gap:8px; align-items:center; }
    .sa-actions .sa-btn[disabled] { opacity: 0.8; cursor: not-allowed; }
  `;

  const isVenueAdmin = window.location.pathname.startsWith("/venue-admin");

  // Global commission (matches your prior save UX)
  const [globalPercent, setGlobalPercent] = useState("10");
  const [globalSaving, setGlobalSaving] = useState(false);
  const [globalMsg, setGlobalMsg] = useState("");

  // Effective commission per venue (for read-only VA view)
  const [effectiveMap, setEffectiveMap] = useState({});


  const saveGlobal = async () => {
    try {
      setGlobalSaving(true);
      setGlobalMsg("");
      await updateGlobalCommission({
        commissionPercent: Number(globalPercent),
        notes: "Updated via Commission admin",
      });
      setGlobalMsg("Saved ✓");
    } catch (e) {
      console.error(e);
      setGlobalMsg("Failed to save");
    } finally {
      setGlobalSaving(false);
      // auto-clear status after 2s
      setTimeout(() => setGlobalMsg(""), 2000);
    }
  };

  // Venues table
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters + client-side pagination
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // server-side paging (if your /venues is paged)
  const [total, setTotal] = useState(0);

  // row edits (venueId -> { percent, status })
  const [edits, setEdits] = useState({});

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const arr = term
      ? venues.filter(
        (v) =>
          v?.name?.toLowerCase().includes(term) ||
          v?.location?.city?.toLowerCase().includes(term) ||
          (v?._id || "").toLowerCase().includes(term),
      )
      : venues;
    return arr;
  }, [venues, q]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const rows = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, page, pageSize]);

  const loadVenues = async (opts = {}) => {
    setLoading(true);
    try {
      const data = await listVenues({
        page: opts.page ?? 1,
        pageSize: opts.pageSize ?? 200, // load enough to filter client-side
        q: opts.q ?? "",
      });
      const items = data.items || [];
      setVenues(items);
      // ✅ Prefill (Superadmin): Global + per-venue overrides
      if (!isVenueAdmin) {
        try {
          const [g, vmap] = await Promise.all([
            getGlobalCommission(),
            listVenueCommissionOverrides(),
          ]);

          if (g?.commissionPercent != null) {
            setGlobalPercent(String(g.commissionPercent));
          }

          const overrides = vmap?.overrides || {};
          setEdits((prev) => {
            const next = { ...prev };
            for (const v of items) {
              const vid = String(v._id);
              if (overrides[vid] != null) {
                next[vid] = { ...(next[vid] || {}), percent: String(overrides[vid]) };
              }
            }
            return next;
          });
        } catch (e) {
          console.error("Prefill commission failed", e);
        }
      }

      setTotal(data.total || items.length);

      // 🔒 For venue admins: fetch effective commission per venue (read-only)
      if (isVenueAdmin) {
        const map = {};
        await Promise.all(
          items.map(async (v) => {
            try {
              const eff = await getEffectiveCommission(v._id);
              map[v._id] = eff?.commissionPercent ?? null;
            } catch (e) {
              console.error("Failed to fetch commission for venue", v._id, e);
              map[v._id] = null;
            }
          })
        );
        setEffectiveMap(map);
      }
    } catch (e) {
      console.error(e);
      toast.error("Failed to load venues");
      setVenues([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    loadVenues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEdit = (venueId, val) =>
    setEdits((p) => ({ ...p, [venueId]: { ...(p[venueId] || {}), percent: val } }));

  const setRowStatus = (venueId, msg) => {
    setEdits((prev) => ({
      ...prev,
      [venueId]: { ...(prev[venueId] || {}), status: msg },
    }));
    if (msg && (msg.includes("Saved") || msg.includes("Failed") || msg.includes("Using global"))) {
      setTimeout(() => {
        setEdits((p) => ({
          ...p,
          [venueId]: { ...(p[venueId] || {}), status: "" },
        }));
      }, 1500);
    }
  };

  const saveVenue = async (venue) => {
    const val = edits[venue._id]?.percent ?? "";
    if (val === "") {
      setRowStatus(venue._id, "Using global (no override set)");
      return;
    }
    try {
      setRowStatus(venue._id, "Saving...");
      await updateVenueCommission(venue._id, {
        commissionPercent: Number(val),
        notes: `Saved for ${venue.name || venue._id}`,
      });
      setRowStatus(venue._id, "Saved ✓");
    } catch (e) {
      console.error(e);
      setRowStatus(venue._id, "Failed");
    }
  };

  return (
    <>
      <style>{styles}</style>

      <div className="sa-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Commission</h1>
      </div>

      {/* Global Commission card */}
      {!isVenueAdmin && (
        <div className="sa-card">
          <div className="sa-row">
            <div className="sa-field">
              <label>Global Commission %</label>
              <input
                type="number"
                min={0}
                max={100}
                step={0.5}
                value={globalPercent}
                onChange={(e) => setGlobalPercent(e.target.value)}
                placeholder="10"
              />
              <small className="sa-hint">Default applied unless a venue override is set.</small>
            </div>

            <div className="sa-actions" style={{ gap: 12, alignItems: "center" }}>
              <button className="sa-btn" onClick={saveGlobal} disabled={globalSaving}>
                {globalSaving ? "Saving..." : "Save"}
              </button>
              {globalMsg && (
                <span className={globalMsg.includes("Saved") || globalMsg.includes("Using global") ? "ok" : "err"}>
                  {globalMsg}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Per-venue Commission */}
      <div className="sa-head-row">
        <h2 className="sa-h2">Per-Venue Commission</h2>
      </div>

      <TableToolbar
        searchTerm={q}
        onSearchChange={(v) => {
          setQ(v);
          setPage(1);
        }}
        searchPlaceholder="Search venue by name/city/id…"
      />

      <div className="sa-card">
        {loading ? (
          <div className="sa-placeholder" style={{ height: 120 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="sa-empty">
            <div>No venues found.</div>
            <button className="sa-btn" onClick={() => loadVenues()}>
              <Plus className="lucide" /> Refresh
            </button>
          </div>
        ) : (
          <div className="sa-table-wrap">
            <table className="sa-table">
              <thead>
                <tr>
                  <th style={{ width: 56 }}>S.N.</th>
                  <th>Venue</th>
                  <th>City</th>
                  <th style={{ width: 160 }}>Commission %</th>
                  {!isVenueAdmin && (
                    <th style={{ textAlign: "right", width: 130 }}>Actions</th>
                  )}
                </tr>
              </thead>

              <tbody>
                {rows.map((v, i) => {
                  const edit = edits[v._id]?.percent ?? "";
                  const status = edits[v._id]?.status ?? "";
                  const statusClass =
                    status.includes("Saved") || status.includes("Using global") ? "ok" :
                      status ? "err" : "";
                  return (
                    <tr key={v._id}>
                      <td>{(page - 1) * pageSize + i + 1}</td>
                      <td>{v.name || "—"}</td>
                      <td>{v.location?.city || "—"}</td>
                      <td>
                        {isVenueAdmin ? (
                          <>
                            <span>
                              {effectiveMap[v._id] != null ? `${effectiveMap[v._id]} %` : "—"}
                            </span>
                            <div className="sa-hint">
                              Effective commission for this venue.
                            </div>
                          </>
                        ) : (
                          <>
                            <input
                              type="number"
                              min={0}
                              max={100}
                              step={0.5}
                              value={edit}
                              onChange={(e) => onEdit(v._id, e.target.value)}
                              placeholder="(use global)"
                            />
                            <div className="sa-hint">Leave blank to use global.</div>
                          </>
                        )}
                      </td>

                      {!isVenueAdmin && (
                        <td className="sa-actions-cell">
                          <div className="sa-actions">
                            <button className="sa-btn" onClick={() => saveVenue(v)}>
                              Save
                            </button>
                            {status && <span className={statusClass}>{status}</span>}
                          </div>
                        </td>
                      )}

                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Paginator
              page={page}
              totalPages={totalPages}
              onPage={setPage}
              pageSize={pageSize}
              onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
              pageSizeOptions={[5, 10, 20, 50, 100]}
            />
            <div className="sa-gray" style={{ marginTop: 8 }}>Total: {total}</div>
          </div>
        )}
      </div>

      {/* Quick effective checker (optional)
      <div className="sa-card"> ... </div> */}
    </>
  );
};

export default CommissionPage;
